import { useQuery, useMutation } from '@apollo/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Banknote,
	CornerDownLeft,
	Ban,
	Loader2,
	ChevronDown,
	ChevronUp,
	Shield
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ulid } from 'ulid';

import { ContractCardSummary } from '../types';
import { formatCurrency, formatDateTime } from '../utils';
import {
	GUARANTEE_TRANSACTIONS_BY_CARD_QUERY,
	CREATE_GUARANTEE_TRANSACTION_MUTATION,
	VOID_GUARANTEE_TRANSACTION_MUTATION,
} from './guarantees.gql';
import type { GuaranteeTransaction } from './guarantee-types';
import { isTransactionVoided, isVoidTransaction } from './guarantee-types';
import { CollectDepositModal } from './collect-deposit-modal';
import { ReturnDepositModal } from './return-deposit-modal';
import { VoidTransactionModal } from './void-transaction-modal';
import { showMutationSuccessToast, showMutationErrorToast } from '@/lib/mutationRefetch';
import { useCurrentFacility } from '@/hooks/useCurrentFacility';

interface ContractGuaranteesTabProps {
	cards: ContractCardSummary[];
	isActive: boolean;
	onContractRefresh?: () => void;
}

export const ContractGuaranteesTab = ({
	cards,
	isActive,
	onContractRefresh,
}: ContractGuaranteesTabProps) => {
	const { t: tC } = useTranslation('contracts');

	if (cards.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-center">
				<Shield className="h-10 w-10 text-muted-foreground mb-3" />
				<p className="text-sm text-muted-foreground">
					{tC('guarantees.noCards')}
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{cards.map((card) => (
				<CardGuaranteeSection
					key={card.contractCardId ?? `${card.cardType}-${card.cardKey}`}
					card={card}
					isTabActive={isActive}
					onContractRefresh={onContractRefresh}
				/>
			))}
		</div>
	);
};

// ─── Per-Card Section ────────────────────────────────────────────────

interface CardGuaranteeSectionProps {
	card: ContractCardSummary;
	isTabActive: boolean;
	onContractRefresh?: () => void;
}

const CardGuaranteeSection = ({ card, isTabActive, onContractRefresh }: CardGuaranteeSectionProps) => {
	const { t: tC } = useTranslation('contracts');
	const { facilityId } = useCurrentFacility();
	const [expanded, setExpanded] = useState(false);

	// Modal state
	const [collectOpen, setCollectOpen] = useState(false);
	const [returnOpen, setReturnOpen] = useState(false);
	const [voidTarget, setVoidTarget] = useState<GuaranteeTransaction | null>(null);

	// Transactions query - only fetch when expanded
	const { data, loading, refetch } = useQuery(GUARANTEE_TRANSACTIONS_BY_CARD_QUERY, {
		variables: { contractCardId: card.contractCardId, first: 50 },
		skip: !card.contractCardId || !expanded || !isTabActive,
		fetchPolicy: 'cache-and-network',
	});

	const transactions: GuaranteeTransaction[] =
		data?.guaranteeTransactionsByContractCard?.nodes ?? [];

	// Mutations
	const [createTransaction, { loading: creating }] = useMutation(
		CREATE_GUARANTEE_TRANSACTION_MUTATION
	);
	const [voidTransaction, { loading: voiding }] = useMutation(
		VOID_GUARANTEE_TRANSACTION_MUTATION
	);

	const hasDeposit = card.depositTransactionId != null;
	const hasReturn = card.returnTransactionId != null;

	const depositStatus = hasReturn
		? 'returned'
		: hasDeposit
			? 'deposited'
			: 'none';

	const handleCollectDeposit = useCallback(
		async (grossAmount: number, notes?: string) => {
			if (!card.contractCardId || !facilityId) return;
			const guaranteeTransactionId = ulid();
			const idempotencyKey = crypto.randomUUID();

			const input = {
				guaranteeTransactionId,
				contractCardId: card.contractCardId,
				guaranteeTransactionTypeId: 'DEPOSIT',
				grossAmount,
				netAmount: grossAmount,
				vatAmount: 0,
				vatPercentage: 0,
				idempotencyKey,
				notes: notes?.trim() || undefined,
			};

			const { data: result } = await createTransaction({
				variables: { input },
			});

			if (result?.createGuaranteeTransaction?.success) {
				showMutationSuccessToast('contracts', 'update', {
					message: tC('guarantees.depositCollected'),
				});
				refetch();
				onContractRefresh?.();
				setCollectOpen(false);
			} else {
				showMutationErrorToast('contracts', 'update', {
					message:
						result?.createGuaranteeTransaction?.errorDescription ??
						tC('guarantees.depositFailed'),
				});
			}
		},
		[card.contractCardId, facilityId, createTransaction, refetch, onContractRefresh, tC]
	);

	const handleReturnDeposit = useCallback(
		async (grossAmount: number, notes?: string) => {
			if (!card.contractCardId || !facilityId) return;
			const guaranteeTransactionId = ulid();
			const idempotencyKey = crypto.randomUUID();

			const input = {
				guaranteeTransactionId,
				contractCardId: card.contractCardId,
				guaranteeTransactionTypeId: 'RETURN',
				grossAmount,
				netAmount: grossAmount,
				vatAmount: 0,
				vatPercentage: 0,
				idempotencyKey,
				notes: notes?.trim() || undefined,
			};

			const { data: result } = await createTransaction({
				variables: { input },
			});

			if (result?.createGuaranteeTransaction?.success) {
				showMutationSuccessToast('contracts', 'update', {
					message: tC('guarantees.depositReturned'),
				});
				refetch();
				onContractRefresh?.();
				setReturnOpen(false);
			} else {
				showMutationErrorToast('contracts', 'update', {
					message:
						result?.createGuaranteeTransaction?.errorDescription ??
						tC('guarantees.returnFailed'),
				});
			}
		},
		[card.contractCardId, facilityId, createTransaction, refetch, onContractRefresh, tC]
	);

	const handleVoidTransaction = useCallback(
		async (targetTransaction: GuaranteeTransaction, notes?: string) => {
			if (!card.contractCardId) return;
			const guaranteeTransactionId = ulid();
			const idempotencyKey = crypto.randomUUID();

			const input = {
				guaranteeTransactionId,
				reversesTransactionId: targetTransaction.guaranteeTransactionId,
				idempotencyKey,
				notes: notes?.trim() || undefined,
			};

			const { data: result } = await voidTransaction({
				variables: { input },
			});

			if (result?.voidGuaranteeTransaction?.success) {
				showMutationSuccessToast('contracts', 'update', {
					message: tC('guarantees.transactionVoided'),
				});
				refetch();
				onContractRefresh?.();
				setVoidTarget(null);
			} else {
				showMutationErrorToast('contracts', 'update', {
					message:
						result?.voidGuaranteeTransaction?.errorDescription ??
						tC('guarantees.voidFailed'),
				});
			}
		},
		[card.contractCardId, voidTransaction, refetch, onContractRefresh, tC]
	);

	return (
		<div className="border rounded-lg bg-card">
			{/* Card header */}
			<div className="flex items-center justify-between px-4 py-3 border-b">
				<div className="flex items-center gap-3">
					<Badge variant="outline" className="font-mono text-xs">
						{card.cardType}
					</Badge>
					<span className="text-sm font-mono">
						{card.cardKey ?? '—'}
					</span>
					<DepositStatusBadge status={depositStatus} />
				</div>
				<div className="flex items-center gap-2">
					{/* Action buttons */}
					{depositStatus === 'none' && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCollectOpen(true)}
							disabled={creating}
						>
							<Banknote className="h-3.5 w-3.5 mr-1.5" />
							{tC('guarantees.collectDeposit')}
						</Button>
					)}
					{depositStatus === 'deposited' && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => setReturnOpen(true)}
							disabled={creating}
						>
							<CornerDownLeft className="h-3.5 w-3.5 mr-1.5" />
							{tC('guarantees.returnDeposit')}
						</Button>
					)}
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setExpanded((prev) => !prev)}
					>
						{expanded ? (
							<ChevronUp className="h-4 w-4" />
						) : (
							<ChevronDown className="h-4 w-4" />
						)}
					</Button>
				</div>
			</div>

			{/* Transaction history (expandable) */}
			{expanded && (
				<div className="px-4 py-3">
					{loading ? (
						<div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
							<Loader2 className="h-4 w-4 animate-spin" />
							{tC('guarantees.loadingTransactions')}
						</div>
					) : transactions.length === 0 ? (
						<div className="text-sm text-muted-foreground py-4">
							{tC('guarantees.noTransactions')}
						</div>
					) : (
						<div className="space-y-2">
							{transactions.map((tx) => (
								<TransactionRow
									key={tx.guaranteeTransactionId}
									transaction={tx}
									onVoid={setVoidTarget}
									isVoiding={voiding}
								/>
							))}
						</div>
					)}
				</div>
			)}

			{/* Modals */}
			<CollectDepositModal
				open={collectOpen}
				onOpenChange={setCollectOpen}
				onConfirm={handleCollectDeposit}
				isSubmitting={creating}
				cardKey={card.cardKey}
			/>

			<ReturnDepositModal
				open={returnOpen}
				onOpenChange={setReturnOpen}
				onConfirm={handleReturnDeposit}
				isSubmitting={creating}
				cardKey={card.cardKey}
				depositAmount={card.depositAmount ?? null}
			/>

			{voidTarget && (
				<VoidTransactionModal
					open={!!voidTarget}
					onOpenChange={(open) => {
						if (!open) setVoidTarget(null);
					}}
					transaction={voidTarget}
					onConfirm={handleVoidTransaction}
					isSubmitting={voiding}
				/>
			)}
		</div>
	);
};

// ─── Deposit Status Badge ────────────────────────────────────────────

const DepositStatusBadge = ({
	status,
}: {
	status: 'none' | 'deposited' | 'returned';
}) => {
	const { t: tC } = useTranslation('contracts');

	switch (status) {
		case 'deposited':
			return (
				<Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-0">
					{tC('guarantees.status.deposited')}
				</Badge>
			);
		case 'returned':
			return (
				<Badge className="bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400 border-0">
					{tC('guarantees.status.returned')}
				</Badge>
			);
		default:
			return (
				<Badge variant="secondary">
					{tC('guarantees.status.noDeposit')}
				</Badge>
			);
	}
};

// ─── Transaction Row ────────────────────────────────────────────────

interface TransactionRowProps {
	transaction: GuaranteeTransaction;
	onVoid: (tx: GuaranteeTransaction) => void;
	isVoiding: boolean;
}

const TransactionRow = ({ transaction, onVoid, isVoiding }: TransactionRowProps) => {
	const { t: tC } = useTranslation('contracts');
	const voided = isTransactionVoided(transaction);
	const isVoid = isVoidTransaction(transaction);

	return (
		<div
			className={`flex items-center justify-between py-2.5 px-3 rounded-md border ${
				voided
					? 'opacity-50 bg-muted/30 border-dashed'
					: 'bg-background border-border/60'
			}`}
		>
			<div className="flex items-center gap-3 min-w-0">
				<TransactionTypeBadge
					typeName={transaction.guaranteeTransactionType.name}
					isVoid={isVoid}
				/>
				<div className="flex flex-col min-w-0">
					<span className="text-sm font-medium">
						{formatCurrency(transaction.grossAmount)}
					</span>
					<span className="text-xs text-muted-foreground truncate">
						{formatDateTime(transaction.createdAt)}
						{transaction.processedByUser && (
							<> · {[transaction.processedByUser.firstName, transaction.processedByUser.lastName].filter(Boolean).join(' ')}</>
						)}
					</span>
				</div>
			</div>

			<div className="flex items-center gap-2 flex-shrink-0">
				{voided && (
					<Badge variant="destructive" className="text-xs">
						{tC('guarantees.voided')}
					</Badge>
				)}
				{!voided && !isVoid && (
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onVoid(transaction)}
						disabled={isVoiding}
						className="text-destructive hover:text-destructive"
					>
						<Ban className="h-3.5 w-3.5 mr-1" />
						{tC('guarantees.void')}
					</Button>
				)}
			</div>
		</div>
	);
};

const TransactionTypeBadge = ({
	typeName,
	isVoid,
}: {
	typeName: string;
	isVoid: boolean;
}) => {
	if (isVoid) {
		return (
			<Badge variant="destructive" className="text-xs font-normal">
				Void
			</Badge>
		);
	}

	const variant =
		typeName === 'Deposit'
			? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
			: typeName === 'Return'
				? 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400'
				: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';

	return (
		<Badge className={`${variant} border-0 text-xs font-normal`}>
			{typeName}
		</Badge>
	);
};
